import { Space } from "antd";
import { DeploymentUnitOutlined } from "@ant-design/icons";
import React, { useState, useMemo } from "react";
import { useLocation, useHistory } from "react-router";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IconNavClaims as ClaimsIcon } from "@tailormed/common-client/assets/svg/IconNavClaims";
import { IconNavEnrollments as EnrollmentsIcon } from "@tailormed/common-client/assets/svg/IconNavEnrollments";
import { IconNavHelp as HelpIcon } from "@tailormed/common-client/assets/svg/IconNavHelp";
import { IconNavHome as HomeIcon } from "@tailormed/common-client/assets/svg/IconNavHome";
import { IconNavPatients as PatientsIcon } from "@tailormed/common-client/assets/svg/IconNavPatients";
import { IconNavTeammate as AIChatBotIcon } from "@tailormed/common-client/assets/svg/IconNavTeammate";
import { IconNavSettings as SettingsIcon } from "@tailormed/common-client/assets/svg/IconNavSettings";
import { AGENTIC_ICON_PURPLE } from "../../constant/colors";
import { defaultFilters } from "../../constant/filters";
import { formPopupsContentKeys } from "../../constant/formPopupsContentKeys";
import { Routes } from "../../constant/routes";
import { selectedViews } from "../../constant/table";
import { useGetSetting } from "../../hooks/getSetting";
import ACTIONS from "../../store/action";
import {
  selectFilterListStatuses,
  selectFormPopups,
  selectUser,
  selectEnvironment,
  selectIsBulkActionDirty,
  selectCanAccessAgenticPlatform
} from "../../store/selector";
import { CustomBadge } from "@tailormed/common-client/component/AppShell";
import { markBulkNavConfirmed } from "../BulkActions/pendingBulkLeaveTeardown";
import { BetaTagContainer, BetaTagText } from "../AIChat/Styles";
import { PENDO_EVENTS } from "../AIChat/Constants";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";
import { SUPPORT_ENTRY_POINTS } from "../../utils/pendo/useStandardizedTracking";
import { trackPendoEvent } from "../../utils/pendo/pendoUtils";
import { Link } from "react-router-dom";

export const MenuItemKeys = Object.freeze({
  home: {
    key: "home",
    subItems: {
      notifications: "notifications",
      tasks: "tasks",
      extra_care: "extra_care",
      saved_lists: selectedViews.savedList
    }
  },
  patients: {
    key: "patients",
    subItems: {
      all_patients: selectedViews.allPatients,
      visit_schedule: selectedViews.visitSchedule,
      high_priority: selectedViews.highPriority,
      patient_actions: selectedViews.patientActions
    }
  },
  enrollments: {
    key: "enrollments",
    subItems: {
      all_enrollments: selectedViews.allApplications,
      pending_renewal: selectedViews.pendingRenewal
    }
  },
  claims: {
    key: "claims",
    subItems: {
      all_claims: selectedViews.allClaims
    }
  },
  ai_chat_bot: {
    key: "ai_chat_bot"
  },
  agentic_platform: {
    key: "agentic_platform",
    subItems: {
      tasks: "agentic_platform_tasks"
    }
  },
  settings: {
    key: "settings"
  },
  help_center: {
    key: "help_center",
    subItems: {
      contact_support: "contact-support"
    }
  }
});

const MenuItemWithBadge = ({ count, title, overflowCount }) => (
  <Space>
    {title}
    {count > 0 && <CustomBadge count={count} overflowCount={overflowCount}></CustomBadge>}
  </Space>
);

const pathToItem = (targetView) => ({ pathname: "/", search: `?selectedView=${targetView}` });

const useGeneralMenusItems = (badgeCounts, selectedView, filtersModified) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const history = useHistory();

  const [
    displayVisitSchedule,
    displayPendingRenewal,
    displayOppsFe,
    displayAllClaims,
    activeLoginMethods,
    displayExtraCare,
    tmAgenticPlatformEnabled
  ] = useGetSetting([
    "automation.visit-schedule.fe-display",
    "automation.pending-renewal.fe-display",
    "automation.opportunities.fe-display",
    "fe.show_claims",
    "patient-experience-active-login-methods",
    "pending-actions.enabled",
    "tmAgenticPlatformEnabled"
  ]);

  const displayPatientActions = Array.isArray(activeLoginMethods) && activeLoginMethods.length > 0;
  const setFormPopupsAct = (visible, contentKey) => dispatch(ACTIONS.setFormPopups(visible, contentKey));
  const setShowSupportAct = () => dispatch(ACTIONS.setShowSupport(true, null, SUPPORT_ENTRY_POINTS.SIDEBAR));
  const { trackEvent } = useDashboardBucketsTrackingEvent();

  const filterListStatuses = useSelector(selectFilterListStatuses);
  const formPopups = useSelector(selectFormPopups);
  const canAccessAgenticPlatform = useSelector(selectCanAccessAgenticPlatform);
  const { visible, contentKey } = formPopups;
  const isNotificationsPopoverOpen = visible && contentKey === formPopupsContentKeys.NOTIFICATIONS ? true : false;
  const isTaskPopoverOpen = visible && contentKey === formPopupsContentKeys.TASKS ? true : false;

  const { pathname } = useLocation();

  // Pending bulk-action "unsaved changes" guard: when the bulk-action panel is
  // dirty (a staged, unapplied edit), leaving it would discard that edit. We stash
  // the target view and show a single confirmation before proceeding. Uses the
  // same dirty selector as BulkActionNavGuard so the guards never disagree.
  // Merely selecting rows is NOT dirty and never triggers the prompt.
  const isBulkActionDirty = useSelector(selectIsBulkActionDirty);
  const [pendingBulkNavTarget, setPendingBulkNavTarget] = useState(null);
  // Holds a deferred navigation that isn't expressed as a target view — e.g. the
  // TailorMed logo / dashboard click, which tears down the view rather than
  // switching to one. Stored as the function to run on "Discard & Leave".
  const [pendingBulkNavAction, setPendingBulkNavAction] = useState(null);

  const performMenuItemNavigation = (targetView, { skipFilterListDiscard = false } = {}) => {
    const clearSavedListRelatedDataAct = () => dispatch(ACTIONS.clearSavedListRelatedDataAction());
    const clearFiltersInputs = () => dispatch(ACTIONS.clearFiltersInputs());
    const setSelectedView = (selectedView) => dispatch(ACTIONS.setSelectedView(selectedView));
    const setFiltersAct = (filters, tableName) => dispatch(ACTIONS.setFiltersAction(filters, tableName));
    const updateFilterListStatusesAct = (filterListID, canModify, showDiscardModal, nextSelectedView) =>
      dispatch(ACTIONS.updateFilterListStatuses(filterListID, canModify, showDiscardModal, nextSelectedView));
    const resetCurrentSavedListStatus = () => dispatch(ACTIONS.markSavedFilterAsChanged(undefined, false));
    const clearTablesDataAct = (view) => dispatch(ACTIONS.clearTablesData(view));

    // When the bulk-action discard was already confirmed, skip the saved-list's
    // own discard modal so the user isn't prompted twice.
    if (!skipFilterListDiscard && pathname.includes(Routes.FILTER_LIST) && filterListStatuses?.canModify) {
      updateFilterListStatusesAct(filterListStatuses?.id, filterListStatuses.canModify, true, targetView);
      // We're surfacing the discard modal instead of navigating, so the click won't change the
      // sidebar selection. Drop focus from the clicked item so it doesn't keep a misleading
      // highlight if the user cancels (the controlled selectedKeys already stays put).
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      return;
    }

    resetCurrentSavedListStatus();
    clearSavedListRelatedDataAct();
    // Paginating a view inside a saved list dual-writes into the shared tablesData slot, so the
    // slot is only suspect when leaving a saved list — reset it then so the bucket starts fresh
    // on page 1 (MVP-30696). On normal bucket-to-bucket switches keep the cached rows: returning
    // to a view shows its data instantly instead of clearing and refetching.
    if (targetView !== selectedViews.savedList && pathname.includes(Routes.FILTER_LIST)) {
      clearTablesDataAct(targetView);
    }

    setSelectedView(targetView);

    const shouldRefetchData = selectedView && filtersModified;

    if (shouldRefetchData) {
      clearFiltersInputs();
      if (selectedView !== selectedViews.savedList) {
        // Note: Rest the previous table view data.
        setFiltersAct(defaultFilters, selectedView);
      } else {
        // Note: fetch the data for the target view, when user is currently at the saved-list view page.
        setFiltersAct(defaultFilters, targetView);
      }
    }
  };

  // Single source of truth: should clicking a menu item targeting `targetView`
  // be intercepted to confirm discarding a dirty bulk action? True only when the
  // panel is dirty AND the click would actually leave the current view.
  // Used both to decide deferral and to block the declarative <Link> push, so
  // the two never disagree (a mismatch leaves the page blank with no popup).
  const shouldGuardNavigationTo = (targetView) =>
    isBulkActionDirty && targetView != null && targetView !== selectedView;

  const handleMenuItemClick = (targetView) => {
    // Clicking the view you're already on is a no-op: don't tear down filters,
    // page, or the open bulk selection. (selectedViews.savedList has its own
    // index page, so a re-click there still re-navigates.)
    if (targetView === selectedView && targetView !== selectedViews.savedList) {
      return;
    }
    if (shouldGuardNavigationTo(targetView)) {
      // Defer: stash the target and show the confirmation. No teardown happens,
      // so filters/table/selection stay intact if the user chooses to stay.
      setPendingBulkNavTarget(targetView);
      return;
    }
    performMenuItemNavigation(targetView);
  };

  // Guard an arbitrary navigation that isn't a target-view switch (the logo /
  // dashboard click). When the panel is dirty we defer the whole action behind
  // the confirmation; otherwise it runs immediately. Mirrors handleMenuItemClick
  // but for a callback instead of a view.
  const guardBulkNavAction = (performNavigation) => {
    if (isBulkActionDirty) {
      // Stash the action (wrapped, so setState doesn't treat the function as an
      // updater) and show the confirmation. No teardown happens until confirm.
      setPendingBulkNavAction(() => performNavigation);
      return;
    }
    performNavigation();
  };

  const confirmBulkNavDiscard = () => {
    const action = pendingBulkNavAction;
    const target = pendingBulkNavTarget;
    setPendingBulkNavAction(null);
    setPendingBulkNavTarget(null);
    // Clear every view's selection so the bulk action bar closes; this also
    // resets the dirty flag (see reducer), so the guard disarms after leaving.
    // Matches the cross-route BulkActionNavGuard discard path.
    dispatch(ACTIONS.clearAllBulkSelections());
    if (action) {
      action();
      return;
    }
    if (target == null) return;

    // "Both dirty at once": the saved list also has unsaved FILTER edits (canModify).
    // Discarding the bulk action must NOT silently drop those filter edits — the user
    // still needs the saved-list "Unsaved Changes" prompt to Save or knowingly discard
    // them. So hand off to performMenuItemNavigation WITHOUT skipFilterListDiscard: it
    // surfaces that discard modal (its existing canModify branch) and does NOT navigate
    // or tear anything down. The modal's own Save/Discard drives the leave from there —
    // the two prompts chain, each owning its dirty state.
    if (filterListStatuses?.canModify) {
      performMenuItemNavigation(target);
      return;
    }

    // Bulk-only dirty: no filter edits to warn about, so this is the terminal leave.
    performMenuItemNavigation(target, { skipFilterListDiscard: true });
    const nextPath = pathToItem(target);
    // Leaving a non-root route for a bucket view is a cross-route push the global
    // BulkActionNavGuard would re-block; pre-confirm it so this one push passes through
    // instead of re-prompting. A same-pathname switch reaches neither guard.
    if (pathname !== nextPath.pathname) {
      markBulkNavConfirmed();
    }
    history.push(nextPath);
  };

  const cancelBulkNavDiscard = () => {
    setPendingBulkNavTarget(null);
    setPendingBulkNavAction(null);
  };

  // Block the declarative <Link>/icon push ONLY for clicks we actually guard,
  // so confirm drives navigation and unguarded clicks navigate normally.
  const makeLinkNavBlocker = (targetView) => (e) => {
    if (shouldGuardNavigationTo(targetView)) {
      e.preventDefault();
    }
  };

  // Memoized so the object reference is stable across renders that don't change
  // the guard state — consumers (e.g. SidebarMenu's handleDashboardClick) put it
  // in useCallback deps, so an unstable reference would recreate those callbacks
  // every render. The handler members close only over dispatch/setters/selectedView,
  // so keying on the guard state + selectedView captures everything that varies.
  const bulkNavGuard = useMemo(
    () => ({
      visible: pendingBulkNavTarget !== null || pendingBulkNavAction !== null,
      shouldGuardNavigationTo,
      guardBulkNavAction,
      makeLinkNavBlocker,
      onConfirm: confirmBulkNavDiscard,
      onCancel: cancelBulkNavDiscard
    }),
    // pathname, filterListStatuses, and filtersModified are read by
    // confirmBulkNavDiscard (via performMenuItemNavigation), so they must
    // invalidate the memo too — else a confirm fired after any of them changed
    // would run against a stale closure (e.g. skip the filter reset).
    [
      pendingBulkNavTarget,
      pendingBulkNavAction,
      selectedView,
      isBulkActionDirty,
      pathname,
      filterListStatuses,
      filtersModified
    ]
  );

  const menuItems = [
    {
      key: MenuItemKeys.home.key,
      title: t("sidebar_menu.home"),
      target: pathToItem(MenuItemKeys.home.subItems.saved_lists),
      targetView: MenuItemKeys.home.subItems.saved_lists,
      icon: (
        <Link
          to={pathToItem(MenuItemKeys.home.subItems.saved_lists)}
          onClick={makeLinkNavBlocker(MenuItemKeys.home.subItems.saved_lists)}
        >
          <HomeIcon />
        </Link>
      ),
      onClick: () => handleMenuItemClick(MenuItemKeys.home.subItems.saved_lists),
      subMenus: [
        {
          key: MenuItemKeys.home.subItems.notifications,
          title: (
            <MenuItemWithBadge
              title={t("sidebar_menu.notifications")}
              count={badgeCounts.notifications}
              overflowCount={99}
            />
          ),
          onClick: () => setFormPopupsAct(!isNotificationsPopoverOpen, formPopupsContentKeys.NOTIFICATIONS),
          display: true
        },
        {
          key: MenuItemKeys.home.subItems.tasks,
          title: <MenuItemWithBadge title={t("sidebar_menu.tasks")} count={badgeCounts.tasks} overflowCount={999} />,
          onClick: () => setFormPopupsAct(!isTaskPopoverOpen, formPopupsContentKeys.TASKS),
          display: true
        },
        {
          key: MenuItemKeys.home.subItems.extra_care,
          title: (
            <MenuItemWithBadge title={t("sidebar_menu.extra_care")} count={badgeCounts.extraCare} overflowCount={99} />
          ),
          target: { pathname: "/" },
          onClick: () => {
            dispatch(ACTIONS.clearSelectedView());
            dispatch(ACTIONS.clearFiltersInputs());
            dispatch(ACTIONS.setFilters(defaultFilters));
          },
          display: !!displayExtraCare
        },
        {
          key: MenuItemKeys.home.subItems.saved_lists,
          title: t("sidebar_menu.saved_lists"),
          target: pathToItem(MenuItemKeys.home.subItems.saved_lists),
          onClick: () => handleMenuItemClick(MenuItemKeys.home.subItems.saved_lists),
          display: true
        }
      ]
    },
    {
      key: MenuItemKeys.patients.key,
      title: t("sidebar_menu.patients"),
      targetView: MenuItemKeys.patients.subItems.all_patients,
      icon: (
        <Link
          to={pathToItem(MenuItemKeys.patients.subItems.all_patients)}
          onClick={makeLinkNavBlocker(MenuItemKeys.patients.subItems.all_patients)}
        >
          <PatientsIcon />
        </Link>
      ),
      target: pathToItem(MenuItemKeys.patients.subItems.all_patients),
      onClick: () => handleMenuItemClick(MenuItemKeys.patients.subItems.all_patients),
      subMenus: [
        {
          key: MenuItemKeys.patients.subItems.all_patients,
          title: t("sidebar_menu.all_patients"),
          target: pathToItem(MenuItemKeys.patients.subItems.all_patients),
          onClick: () => handleMenuItemClick(MenuItemKeys.patients.subItems.all_patients),
          display: true
        },
        {
          key: MenuItemKeys.patients.subItems.visit_schedule,
          title: t("sidebar_menu.visit_schedule"),
          target: pathToItem(MenuItemKeys.patients.subItems.visit_schedule),
          onClick: () => handleMenuItemClick(MenuItemKeys.patients.subItems.visit_schedule),
          display: displayVisitSchedule
        },
        {
          key: MenuItemKeys.patients.subItems.high_priority,
          title: t("sidebar_menu.high_priority"),
          target: pathToItem(MenuItemKeys.patients.subItems.high_priority),
          onClick: () => handleMenuItemClick(MenuItemKeys.patients.subItems.high_priority),
          display: displayOppsFe
        },
        {
          key: MenuItemKeys.patients.subItems.patient_actions,
          title: t("sidebar_menu.patient_enroll_sent"),
          target: pathToItem(MenuItemKeys.patients.subItems.patient_actions),
          onClick: () => handleMenuItemClick(MenuItemKeys.patients.subItems.patient_actions),
          display: displayPatientActions
        }
      ]
    },
    {
      key: MenuItemKeys.enrollments.key,
      title: t("sidebar_menu.enrollments"),
      target: pathToItem(selectedViews.allApplications),
      targetView: selectedViews.allApplications,
      icon: (
        <Link
          to={pathToItem(selectedViews.allApplications)}
          onClick={makeLinkNavBlocker(selectedViews.allApplications)}
        >
          <EnrollmentsIcon />
        </Link>
      ),
      onClick: () => handleMenuItemClick(selectedViews.allApplications),
      subMenus: [
        {
          key: MenuItemKeys.enrollments.subItems.all_enrollments,
          title: t("sidebar_menu.all_enrollments"),
          target: pathToItem(selectedViews.allApplications),
          onClick: () => handleMenuItemClick(selectedViews.allApplications),
          display: true
        },
        {
          key: MenuItemKeys.enrollments.subItems.pending_renewal,
          title: t("sidebar_menu.pending_renewals"),
          target: pathToItem(MenuItemKeys.enrollments.subItems.pending_renewal),
          onClick: () => handleMenuItemClick(MenuItemKeys.enrollments.subItems.pending_renewal),
          display: displayPendingRenewal
        }
      ]
    },
    {
      key: MenuItemKeys.claims.key,
      title: t("sidebar_menu.claims"),
      target: pathToItem(MenuItemKeys.claims.subItems.all_claims),
      targetView: MenuItemKeys.claims.subItems.all_claims,
      icon: (
        <Link
          to={pathToItem(MenuItemKeys.claims.subItems.all_claims)}
          onClick={makeLinkNavBlocker(MenuItemKeys.claims.subItems.all_claims)}
        >
          <ClaimsIcon />
        </Link>
      ),
      onClick: () => handleMenuItemClick(MenuItemKeys.claims.subItems.all_claims),
      subMenus: [
        {
          key: MenuItemKeys.claims.subItems.all_claims,
          target: pathToItem(MenuItemKeys.claims.subItems.all_claims),
          title: t("sidebar_menu.all_claims"),
          onClick: () => handleMenuItemClick(MenuItemKeys.claims.subItems.all_claims),
          display: displayAllClaims
        }
      ]
    },
    {
      key: MenuItemKeys.ai_chat_bot.key,
      title: (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          {t("sidebar_menu.ai_chat_bot")}
          <BetaTagContainer style={{ marginLeft: "auto", marginRight: "8px" }}>
            <BetaTagText>BETA</BetaTagText>
          </BetaTagContainer>
        </div>
      ),
      target: { pathname: "/ai-chat-bot/new" },
      icon: (
        <Link to={{ pathname: "/ai-chat-bot/new" }}>
          <AIChatBotIcon />
        </Link>
      ),
      onClick: () => {
        trackEvent(PENDO_EVENTS.CHATBOT_INITIALIZED);
        dispatch(ACTIONS.setAiChatBotIntroPanelShown(true));
        handleMenuItemClick(MenuItemKeys.ai_chat_bot.key);
        if (history.location.pathname !== "/ai-chat-bot/new") {
          history.push({ pathname: "/ai-chat-bot/new" });
        }
      },
      subMenus: []
    },
    // Gated by BOTH the per-site `tmAgenticPlatformEnabled` setting AND the
    // user gate (a TMComplete user with an allowed role — operations manager /
    // financial navigator / insights), so other users on an enabled site don't
    // see this nav entry. The Tasks sub-menu mirrors the Patients > sub-items pattern.
    ...(tmAgenticPlatformEnabled && canAccessAgenticPlatform
      ? [
          {
            key: MenuItemKeys.agentic_platform.key,
            title: (
              <div style={{ display: "flex" }}>
                {t("sidebar_menu.agentic_platform")}
                <BetaTagContainer style={{ position: "relative", left: "30px" }}>
                  <BetaTagText>BETA</BetaTagText>
                </BetaTagContainer>
              </div>
            ),
            target: { pathname: Routes.AGENTIC_PLATFORM_TASKS },
            icon: (
              <Link to={{ pathname: Routes.AGENTIC_PLATFORM_TASKS }}>
                <DeploymentUnitOutlined style={{ fontSize: 20, color: AGENTIC_ICON_PURPLE }} />
              </Link>
            ),
            onClick: () => {
              history.push(Routes.AGENTIC_PLATFORM_TASKS);
            },
            // Dashboard is intentionally NOT listed here — it's reachable only by
            // navigating directly to /agentic-platform/dashboard (the SecuredRouter
            // route stays mounted). Tasks is the only nav entry.
            subMenus: [
              {
                key: MenuItemKeys.agentic_platform.subItems.tasks,
                title: t("sidebar_menu.agentic_platform_tasks"),
                target: { pathname: Routes.AGENTIC_PLATFORM_TASKS },
                onClick: () => history.push(Routes.AGENTIC_PLATFORM_TASKS),
                display: true
              }
            ]
          }
        ]
      : []),
    {
      key: MenuItemKeys.settings.key,
      title: t("sidebar_menu.settings"),
      target: { pathname: "/settings" },
      icon: <SettingsIcon />,
      onClick: () => {
        try {
          trackPendoEvent(dispatch, "Side_Menu_Settings", {
            username: user?.email,
            account
          });
        } catch (error) {
          console.error("Error dispatching side menu settings Pendo event:", error);
        }
        history.push("/settings");
      },
      subMenus: []
    },
    {
      key: MenuItemKeys.help_center.key,
      title: t("sidebar_menu.contact_support"),
      icon: <HelpIcon />,
      onClick: () => {
        setFormPopupsAct(false, null);
        setShowSupportAct();
      },
      subMenus: [
        {
          key: MenuItemKeys.help_center.subItems.contact_support,
          title: t("sidebar_menu.contact_support"),
          onClick: () => {
            setFormPopupsAct(false, null);
            setShowSupportAct();
          },
          display: true
        }
      ]
    }
  ];

  return { menuItems, bulkNavGuard };
};

export { useGeneralMenusItems };
